import Service, { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import { trackedFunction } from 'reactiveweb/function';

function arrayIntersect(target = [], source = []) {
  return target.filter((property) => {
    return source.includes(property);
  });
}

function mergeActions(target = {}, source = {}) {
  return {
    ...truthyValues(source),
    ...truthyValues(target),
  };
}

function mergeOptions(target = {}, source = {}) {
  let result = {};
  let keys = new Set([...Object.keys(source), ...Object.keys(target)]);

  keys.forEach((key) => {
    if (source[key] || target[key]) {
      result[key] = true;
    } else if (source[key] === false && target[key] === false) {
      result[key] = false;
    }
  });

  return result;
}

function truthyValues(object = {}) {
  let result = {};

  Object.keys(object).forEach((key) => {
    if (object[key]) {
      result[key] = object[key];
    }
  });

  return result;
}

export default class PermissionService extends Service {
  // Services
  @service currentUser;
  @service filter;

  // Getter and setter
  get user() {
    return this.filter?.globalUser ?? this.currentUser.model;
  }

  @cached
  get userPermissions() {
    let userPermissions = [];

    this.userRoles.forEach((userRole) => {
      let userRolePermissions = userRole.permissions ?? [];

      userRolePermissions.forEach((userRolePermission) => {
        let existingPermission = userPermissions.find((userPermission) => {
          return userPermission.for === userRolePermission.for;
        });

        if (existingPermission) {
          existingPermission.excludedProperties = arrayIntersect(
            userRolePermission.excludedProperties,
            existingPermission.excludedProperties,
          );

          existingPermission.actions = mergeActions(
            userRolePermission.actions,
            existingPermission.actions,
          );

          existingPermission.options = mergeOptions(
            userRolePermission.options,
            existingPermission.options,
          );
        } else {
          userPermissions.push(structuredClone(userRolePermission));
        }
      });
    });

    return userPermissions;
  }

  get userRoles() {
    return this.userRolesState.value || [];
  }

  get sftpUsers() {
    return this.sftpUsersState.value || [];
  }

  // Functions
  for(recordType) {
    if (this.user?.get('isAdmin')) {
      return {
        for: recordType,
        actions: {
          create: true,
          read: true,
          update: true,
          delete: true,
          unlockEntity: true,
          blockEntity: true,
        },
        excludedProperties: [],
      };
    }

    return this.userPermissions.find((userPermission) => {
      return userPermission.for === recordType;
    });
  }

  async ensureReady() {
    if (this.user?.get('isAdmin')) {
      return;
    }

    await this.userRolesState.promise;
  }

  // Tracked Functions
  sftpUsersState = trackedFunction(this, async () => {
    return await this.user?.sftpUsers;
  });

  userRolesState = trackedFunction(this, async () => {
    return await this.user?.userRoles;
  });
}
